import { ButtonType } from '@client/utils/constants';
import { PayPalPaymentOption } from '@client/typings/payment';
import { ButtonConfigExtensionFromSettings } from '@client/components/Settings/utils';
import { DynamicImageWithStatus } from '@client/typings/Common';

export enum PaymentButtonType {
  Link = 'LINK',
  Button = 'BUTTON'
}

export enum CheckoutButtonType {
  Stacked = 'stacked',
  Single = 'single'
}

export enum CheckoutButtonLayout {
  Vertical = 'vertical',
  Horizontal = 'horizontal'
}

export enum CheckoutButtonShape {
  Rect = 'rect',
  Pill = 'pill',
  Sharp = 'sharp'
}

export enum CheckoutButtonColor {
  Gold = 'gold',
  Blue = 'blue',
  Silver = 'silver',
  White = 'white',
  Black = 'black'
}

export enum CheckoutSingleButtonColorType {
  Recommended = 'recommended',
  White = 'white',
  Black = 'black',
  Custom = 'custom'
}

export enum SingleButtonTextType {
  Buynow = 'buynow',
  Paynow = 'paynow',
  Custom = 'custom'
}

export enum CheckoutPaypalButtonText {
  Paypal = 'paypal',
  Checkout = 'checkout',
  Buynow = 'buynow',
  Pay = 'pay',
  Installment = 'installment'
}

export enum CheckoutButtonTagline {
  True = 'true',
  False = 'false'
}

export enum CheckoutButtonSize {
  Large = 'large',
  Medium = 'medium',
  Small = 'small'
}

export enum CheckoutButtonTextType {
  Checkout = 'checkout',
  Proceed = 'proceed',
  Pay = 'pay',
  Custom = 'custom'
}

export enum NoShipping {
  /**
   * Prompt for an address, but do not require one
   */
  // PromptOptional = '0',
  /**
   * Do not prompt for an address
   */
  NoPrompt = '1',
  /**
   * Prompt for an address and require one
   */
  PromptRequired = '2'
}

export enum ShippingPreference {
  /**
   * Shipping from profile
   */
  FromProfile = 'shipping_from_profile',
  /**
   * Free shipping
   */
  Free = 'free_shipping',
  /**
   * Shipping not applicable
   */
  NotApplicable = 'shipping_not_applicable',
  /**
   * Custom shipping
   */
  Custom = 'custom_shipping'
}

export enum TaxRatePreference {
  /**
   * Tax rate from profile
   */
  FromProfile = 'tax_rate_from_profile',
  /**
   * Tax rate not applicable
   */
  NotApplicable = 'tax_rate_not_applicable',
  /**
   * Custom tax rate
   */
  Custom = 'custom_tax_rate'
}

export enum ReturnUrlType {
  /**
   * No return url
   */
  None = 'no_return_url',
  /**
   * Custom return url
   */
  Custom = 'custom_return_url'
}

export enum CheckoutPaymentOptionsOtherThanPayPal {
  Venmo = 'venmo',
  PayLater = 'paylater',
  ApplePay = 'applepay',
  Card = 'card'
}

export type CheckoutPaymentOptions = PayPalPaymentOption | CheckoutPaymentOptionsOtherThanPayPal;

/**
 * Key values that are specific to client side and will not send to server
 */
type ClientConfig = {};

/**
 * Key values that we want to pass to the server from the client
 */
type OverrideFlagsConfig = {
  /**
   * [DTNCPS-1141][TEMP] Adding in to handle overriding the 3-buttons elmo for our functional tests
   */
  is3ButtonsEligible?: boolean;
};

export type CheckoutButtonStackedConfig = {
  /**
   * Checkout button layout
   */
  layout?: `${CheckoutButtonLayout}`;
  /**
   * Checkout button shape
   */
  shape?: `${CheckoutButtonShape}`;
  /**
   * Checkout button color
   */
  color?: `${CheckoutButtonColor}`;
  /**
   * Paypal checkout button text
   */
  paypalButtonText?: `${CheckoutPaypalButtonText}`;
  /**
   * Checkout button tagline
   * @deprecated
   * No longer being used in phase 2
   */
  tagline: `${CheckoutButtonTagline}`;
  /**
   * Checkout button text type
   */
  checkoutButtonTextType?: `${CheckoutButtonTextType}`;
  /**
   * Checkout button custom text
   */
  checkoutButtonCustomText?: string;
  /**
   * Checkout button size
   */
  size?: `${CheckoutButtonSize}`;
};

export type CheckoutButtonSingleConfig = {
  /**
   * Checkout single button color type
   */
  singleButtonColorType?: `${CheckoutSingleButtonColorType}`;
  /**
   * Checkout single button color type in hex
   * E.g: "FFD140"
   */
  singleButtonColor?: string;
  /**
   * Checkout button text type
   */
  singleButtonTextType?: `${SingleButtonTextType}`;
  /**
   * Checkout button custom text
   */
  singleButtonCustomText: string;
  /**
   * Checkout single button color type
   */
  singleButtonSize?: `${CheckoutButtonSize}`;
};

type CheckoutButtonConfig = CheckoutButtonStackedConfig &
  CheckoutButtonSingleConfig & {
    /**
     * Checkout button type
     */
    checkoutButtonType: `${CheckoutButtonType}`;

    /**
     * Enable button label customization
     */
    enableButtonLabels?: boolean;
  };

type ThankYouConfig = {
  /**
   * Return url type
   */
  returnUrlType: `${ReturnUrlType}`;
  /**
   * Custom return url
   */
  customReturnUrl: string;
  /**
   * Whether or not custom return url is enabled
   */
  enableCustomReturnUrl: boolean;
};

export type VariantOption = {
  images?: DynamicImageWithStatus[];
  optionName: string;
  price?: string;
};

export type Variant = {
  variantName: string;
  variantOptions: Array<VariantOption>;
};

export type VariantsConfig = {
  isEnableVariants: boolean;
  isEnablePricePerVariant: boolean;
  variants: Array<Variant>;
};

export type TransformedVariantOption = {
  images?: string;
  optionName: string;
  price?: string;
};

export type TransformedVariant = {
  variantName: string;
  variantOptions: Array<TransformedVariantOption>;
};

export type ButtonConfigBase = ThankYouConfig & {
  /**
   * Button type
   */
  buttonType: `${ButtonType}`;
  /**
   * Product name
   */
  itemName: string;
  /**
   * Product description
   */
  productDescription: string;
  /**
   * Currency code
   * e.g: USD
   */
  currencyCode: string;
  /**
   * Shipping options toggle - optional
   */
  enableShipping?: boolean;
  /**
   * Custom shipping rate
   */
  shipping: string;
  /**
   * Shipping preference - optional
   */
  shippingPreference?: `${ShippingPreference}`;
  /**
   * Tax options toggle - optional
   */
  enableTax?: boolean;
  /**
   * Custom tax rate
   */
  taxRate: string;
  /**
   * Tax rate preference - optional
   */
  taxRatePreference?: `${TaxRatePreference}`;
  /**
   * Whether to not collect shipping address
   */
  isNotCollectShippingAddress: boolean;
  /**
   * Product id to show in order details. Does not show to buyer
   */
  productId: string;
  /**
   * Whether or not product id is enabled for collection
   */
  enableProductId?: boolean;
  /**
   * Memo notes for the buyers
   */
  memoLabel: string;
  /**
   * Determines whether or not customer memo is mandatory for the buyer
   */
  mandateCustomerMemo: boolean;
  /**
   * Signifies whether or not customer memo should be collected
   */
  enableCustomerMemo?: boolean;
  /**
   * Product dynamic images
   */
  productImagesWithStatus?: DynamicImageWithStatus[];
  /**
   * Whether or not product images toggle is enabled
   */
  enableProductImagesToggle?: boolean;
} & VariantsConfig;

type QuantityConfig = {
  quantityOption: string;
  isEnableQuantityOption: boolean;
};

export type BuyNowButtonConfig = CheckoutButtonConfig & {
  /**
   * Preference type to guide the merchant through button creation experience
   */
  paymentButtonType: `${PaymentButtonType}`;
};

export type FixedButtonConfig = QuantityConfig & {
  amount: string;
};

export type VariableButtonConfig = {
  invoiceLabel: string;
  /**
   * Determines whether or not invoice id is mandatory for the buyer
   */
  mandateInvoiceId: boolean;
  /**
   * Enable whether or not invoice id is collected
   */
  enableInvoiceId?: boolean;
  amountLabel: string;
};

export type ButtonConfig = ClientConfig &
  OverrideFlagsConfig &
  ButtonConfigBase &
  BuyNowButtonConfig &
  FixedButtonConfig &
  VariableButtonConfig &
  ButtonConfigExtensionFromSettings;

export type CommonButtonConfig = ButtonConfigBase & FixedButtonConfig;

export enum AccordionName {
  BUTTON_PREFERENCE = 'buttonPreference',
  CREATE_PRODUCT = 'createProduct',
  CUSTOMIZE_THANK_YOU = 'customizeThankYou'
}

export enum EditorTabName {
  PRODUCT = 'product',
  CHECKOUT = 'checkout',
  CONFIRMATION = 'confirmation'
}
